import { LitElement, html, PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { setSmoothLoading } from '@amityeko/pbc-core/dist/utilities/loading';
import { withAuth } from '@amityeko/pbc-core/dist/providers/auth/consumer-mixin';

import '../card';

import type { CardListResponse, CardList } from './types';

import styles from './styles';

@customElement('rnr-client-badge-card-list')
export default class RnRClientBadgeCardList extends withAuth(LitElement) {
  static styles = styles;

  @property({ type: Number })
  limit?: number;

  @property()
  receiverId?: string;

  @state()
  selectedCardId?: string;

  @state()
  cardList: CardList[] = [];

  @state()
  isImageloading: boolean = false;

  protected updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    if (changedProperties.has('receiverId')) {
      this.getCardList();
    }

    if (changedProperties.has('auth') && this.auth) {
      const prevAuth: typeof this.auth = changedProperties.get('auth');
      if (this.auth.isAuthenticated && !prevAuth?.isAuthenticated) {
        this.getCardList();
      }
    }
  }

  private _reset() {
    this.selectedCardId = undefined;
    this.cardList = [];
  }

  private _produceImagePromise(card?: CardListResponse): Promise<CardList | null> {
    if (!this.auth?.http || !card || !card.thumbnail) return Promise.resolve(null);
    this.isImageloading = true;

    return new Promise(resolve => {
      this.auth?.http?.media(`/api/v1/media/images/${card.thumbnail}`).then((src?: string) => {
        if (src) {
          resolve({
            id: card.id,
            imageSrc: src,
            cardTitle: card.name,
            points: card.points,
            count: card.count,
          });
          setSmoothLoading(1000).then((loadingState: boolean) => {
            this.isImageloading = loadingState;
          });
        } else {
          resolve(null);
          setSmoothLoading(1000).then((loadingState: boolean) => {
            this.isImageloading = loadingState;
          });
        }
      });
    });
  }

  private async getCardList() {
    if (!this.auth?.http || !this.receiverId) return;

    this._reset();

    try {
      // call recognition achievement list api
      // TODO: To add other parameters
      const rawCardList = await this.auth?.http?.get('/api/v1/recognitions/achievements', {
        receiverId: this.receiverId,
        limit: this.limit,
      });

      if (!Array.isArray(rawCardList?.data)) return;

      const transformedPromises = rawCardList?.data.map((card: CardListResponse) =>
        this._produceImagePromise(card),
      );

      Promise.all(transformedPromises).then((processCardList: Array<CardList | null> = []) => {
        this.cardList = processCardList.filter((card): card is CardList => Boolean(card));

        //if cardList is not empty, emit the total count to parent component
        if (this.cardList.length > 0) {
          const totalCount = this.cardList?.reduce(
            (totalCount, achievement) => totalCount + achievement?.count,
            0,
          );
          emit(this, 'rnr-change-total-count', { detail: { totalCount } });
        } else {
          emit(this, 'rnr-change-total-count', { detail: { totalCount: 0 } });
        }
      });
    } catch (e: any) {
      emit(this, 'amityeko-notification--toast', {
        detail: { message: e?.error?.message, variant: 'error' },
      });
    }
  }

  private _onCardClick(card: CardList) {
    this.selectedCardId = card?.id;
    emit(this, 'rnr-select-card', { detail: { ...card } });
  }

  protected render() {
    return html`
      <div class="achievement-card-container">
        ${this.cardList?.length
          ? html`${repeat(
              this.cardList,
              card => card.id,
              card => html`
                <rnr-client-badge-card
                  .selected=${card.id === this.selectedCardId}
                  .imageSrc=${card.imageSrc}
                  .cardTitle=${card.cardTitle}
                  .points=${card.count}
                  ?icon=${false}
                  ?isLoading=${this.isImageloading}
                  @click=${() => this._onCardClick(card)}
                ></rnr-client-badge-card>
              `,
            )}`
          : null}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-client-badge-card-list': RnRClientBadgeCardList;
  }
}
